import {
  BaseQueryFn,
  FetchArgs,
  fetchBaseQuery,
  FetchBaseQueryError,
} from '@reduxjs/toolkit/query';
import { RootState } from 'app/redux';
import { versionSlice } from 'app/redux/versionSlice';
import { environment } from 'environment';
import { authSlice, AuthState } from 'features/auth/authSlice';
import { handleExpiredSession } from './utils/handleExpiredSession';
import { customStatusCodes } from './utils/customStatusCodes';

/**
 * An object containing header version information used in both http
 * requests and responses.
 *
 * @param name The string name of the header.
 * @param noVersionValue The string value to expect when the client was
 * unable to obtain the server version before making its request.
 *
 * @remarks On first render, the client can issue requests without knowing the
 * server version yet. For that scenario, we pass along a string value the
 * server can expect so that the server does not conclude a version mismatch.
 */
export const versionHeader = Object.freeze({
  name: 'server-version',
  noVersionValue: 'null',
});

const baseQuery = fetchBaseQuery({
  baseUrl: environment.apiRoute,
  prepareHeaders: (headers: Headers, { getState }) => {
    const state = getState() as RootState;
    const { firstLoadVersion } = state.versionSlice;
    const { token } = state.auth;

    if (token) {
      headers.set('authorization', `Bearer ${token}`);
    }

    headers.set(
      versionHeader.name,
      firstLoadVersion ?? versionHeader.noVersionValue,
    );

    return headers;
  },
});

export const customBaseQuery: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError
> = async (args, api, extraOptions) => {
  const { dispatch, endpoint, getState } = api;
  const { auth } = getState() as { auth: AuthState };
  const { removeEndpointToRefetch } = authSlice.actions;

  const result = await baseQuery(args, api, extraOptions);
  const { error, meta } = result;

  if (meta?.response) {
    const { response } = meta;
    const responseVersion = response.headers?.get(versionHeader.name);

    if (responseVersion) {
      // Check to see if the refresh button should be shown.
      dispatch(versionSlice.actions.compareVersions(responseVersion));
    }

    if (response.ok && auth.endpointsToRefetch.includes(endpoint)) {
      dispatch(removeEndpointToRefetch(endpoint));
    }

    if (!response.ok && error?.status === customStatusCodes.SESSION_EXPIRED) {
      handleExpiredSession(api, result);
    }
  }

  return result;
};
